import { FiUsers, FiBook, FiTrendingUp, FiTrendingDown, FiClock, FiAlertTriangle, FiUserMinus, FiEdit, FiX, FiDollarSign } from 'react-icons/fi';
import SubscriptionAuditPanel from './SubscriptionAuditPanel';

export default function StatsTab({
  dashboardStats, loadingStats, fetchDashboardStats,
  borrowRules, loadingBorrowRules, newRuleCategory, newRuleDays, addingRule, editingRule, editRuleDays,
  setNewRuleCategory, setNewRuleDays, setEditingRule, setEditRuleDays,
  handleAddBorrowRule, handleUpdateBorrowRule, handleDeleteBorrowRule,
  fixingInventory, handleFixAllInventory,
  sendingRenewalReminders, handleSendRenewalReminders,
  sendingExpiredNotices, handleSendExpiredNotices,
  fixingStaff, handleFixStaffSubscriptions,
  recalculatingPayments, handleRecalculateAdvancePayments,
  getAuthHeader
}) {
  return (
          <div className="space-y-6">
            {loadingStats ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <p className="text-gray-600">Loading dashboard statistics...</p>
              </div>
            ) : dashboardStats ? (
              <>
                {/* Header: title, last-updated, jump nav, refresh */}
                <div className="bg-white rounded-xl shadow-lg px-6 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="stats-header">
                  <div>
                    <h2 className="font-bold text-gray-800 text-lg">Library stats</h2>
                    <p className="text-xs text-gray-400">Updated {new Date(dashboardStats.generated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {[['stats-money', '💰 Money'], ['stats-books', '📚 Books'], ['stats-members', '👥 Members'], ['stats-rules', '⏱ Borrow rules'], ['stats-tools', '🧰 Tools']].map(([id, label]) => (
                      <a key={id} href={`#${id}`} className="px-3 py-1.5 bg-gray-100 hover:bg-amber-100 text-gray-700 rounded-full font-medium transition-colors" data-testid={`stats-nav-${id}`}>{label}</a>
                    ))}
                    <button onClick={fetchDashboardStats} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-semibold" data-testid="stats-refresh">↻ Refresh</button>
                  </div>
                </div>

                {/* Overview Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FiUsers className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-600">Total Members</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{dashboardStats.overview.total_members}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {dashboardStats.overview.active_subscriptions} active ({dashboardStats.overview.subscription_rate}%)
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <FiBook className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-sm text-gray-600">Books in Catalog</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{dashboardStats.overview.total_books}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {dashboardStats.borrowing.currently_borrowed} currently borrowed
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <FiTrendingUp className="w-5 h-5 text-amber-600" />
                      </div>
                      <span className="text-sm text-gray-600">This Month</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{dashboardStats.membership.new_this_month}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      new signups ({dashboardStats.membership.new_last_month} last month)
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FiClock className="w-5 h-5 text-purple-600" />
                      </div>
                      <span className="text-sm text-gray-600">Borrows This Month</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{dashboardStats.borrowing.borrows_this_month}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {dashboardStats.borrowing.returns_this_month} returns
                    </p>
                  </div>
                </div>

                {/* Pending Actions Alert */}
                {(dashboardStats.pending_actions.overdue_books > 0 || 
                  dashboardStats.pending_actions.extension_requests > 0 || 
                  dashboardStats.pending_actions.pending_holds > 0) && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                      <FiAlertTriangle className="w-5 h-5" />
                      Requires Attention
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {dashboardStats.pending_actions.overdue_books > 0 && (
                        <div className="bg-white rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-red-600">{dashboardStats.pending_actions.overdue_books}</p>
                          <p className="text-xs text-red-700">Overdue Books</p>
                        </div>
                      )}
                      {dashboardStats.pending_actions.pending_holds > 0 && (
                        <div className="bg-white rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-yellow-600">{dashboardStats.pending_actions.pending_holds}</p>
                          <p className="text-xs text-yellow-700">Pending Holds</p>
                        </div>
                      )}
                      {dashboardStats.pending_actions.extension_requests > 0 && (
                        <div className="bg-white rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-600">{dashboardStats.pending_actions.extension_requests}</p>
                          <p className="text-xs text-blue-700">Extension Requests</p>
                        </div>
                      )}
                      {dashboardStats.pending_actions.recommendation_requests > 0 && (
                        <div className="bg-white rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-purple-600">{dashboardStats.pending_actions.recommendation_requests}</p>
                          <p className="text-xs text-purple-700">Recommendation Requests</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Overdue Books Section (collapsible) */}
                {dashboardStats.overdue_books && dashboardStats.overdue_books.length > 0 && (
                  <details className="bg-white rounded-xl shadow-lg p-6" data-testid="overdue-books-details">
                    <summary className="font-bold text-gray-800 flex items-center gap-2 cursor-pointer select-none list-none">
                      <FiAlertTriangle className="w-5 h-5 text-red-500" />
                      Overdue Books ({dashboardStats.overdue_books.length})
                      {dashboardStats.financials.total_outstanding_fees > 0 && (
                        <span className="text-xs font-semibold bg-red-100 text-red-700 rounded-full px-2 py-0.5">₹{dashboardStats.financials.total_outstanding_fees.toFixed(0)} outstanding</span>
                      )}
                      <span className="ml-auto text-xs font-normal text-gray-400">tap to expand ▾</span>
                    </summary>
                    <div className="mt-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-red-50">
                            <th className="text-left py-2 px-3">Member</th>
                            <th className="text-left py-2 px-3">Book</th>
                            <th className="text-left py-2 px-3">Due Date</th>
                            <th className="text-left py-2 px-3">Days Overdue</th>
                            <th className="text-left py-2 px-3">Late Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.overdue_books.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-3">
                                <p className="font-medium">{item.member_name}</p>
                                <p className="text-xs text-gray-500">{item.membership_number}</p>
                              </td>
                              <td className="py-2 px-3">
                                <p className="font-medium">{item.book_title}</p>
                                <p className="text-xs text-gray-500">{item.book_authors?.join(', ')}</p>
                              </td>
                              <td className="py-2 px-3">{new Date(item.due_date).toLocaleDateString()}</td>
                              <td className="py-2 px-3">
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">
                                  {item.days_overdue} days
                                </span>
                              </td>
                              <td className="py-2 px-3 font-semibold text-red-600">₹{item.late_fee}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {dashboardStats.financials.total_outstanding_fees > 0 && (
                      <div className="mt-4 pt-4 border-t flex justify-between items-center">
                        <span className="text-gray-600">Total Outstanding Late Fees:</span>
                        <span className="text-xl font-bold text-red-600">₹{dashboardStats.financials.total_outstanding_fees.toFixed(2)}</span>
                      </div>
                    )}
                    </div>
                  </details>
                )}

                {/* Monthly Earnings (last 6 months) */}
                {dashboardStats.financials?.monthly_earnings?.length > 0 && (
                  <div id="stats-money" className="bg-white rounded-xl shadow-lg p-6 scroll-mt-4" data-testid="monthly-earnings-section">
                    <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                      <FiDollarSign className="w-5 h-5 text-emerald-600" />
                      Monthly Earnings (Last 6 Months)
                    </h3>
                    <p className="text-xs text-gray-500 mb-4">
                      Memberships, pay-per-book rentals, and add-on events combined.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                            <th className="py-2 pr-3">Month</th>
                            <th className="py-2 pr-3 text-right">Memberships</th>
                            <th className="py-2 pr-3 text-right">Rentals</th>
                            <th className="py-2 pr-3 text-right">Add-ons</th>
                            <th className="py-2 pr-3 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...dashboardStats.financials.monthly_earnings].reverse().map((m) => (
                            <tr
                              key={m.month}
                              className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                              data-testid={`monthly-earning-row-${m.month.replace(/\s+/g, '-')}`}
                            >
                              <td className="py-2 pr-3 font-medium text-gray-800">{m.month}</td>
                              <td className="py-2 pr-3 text-right text-gray-700">
                                ₹{Number(m.memberships || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 pr-3 text-right text-gray-700">
                                ₹{Number(m.rentals || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 pr-3 text-right text-gray-700">
                                ₹{Number(m.addons || 0).toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 pr-3 text-right font-semibold text-emerald-700">
                                ₹{Number(m.total || 0).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-300 bg-emerald-50">
                            <td className="py-2 pr-3 font-bold text-gray-800">6-Month Total</td>
                            {(() => {
                              const totals = dashboardStats.financials.monthly_earnings.reduce(
                                (acc, m) => ({
                                  memberships: acc.memberships + (m.memberships || 0),
                                  rentals: acc.rentals + (m.rentals || 0),
                                  addons: acc.addons + (m.addons || 0),
                                  total: acc.total + (m.total || 0),
                                }),
                                { memberships: 0, rentals: 0, addons: 0, total: 0 }
                              );
                              return (
                                <>
                                  <td className="py-2 pr-3 text-right font-semibold text-gray-800">
                                    ₹{Number(totals.memberships).toLocaleString('en-IN')}
                                  </td>
                                  <td className="py-2 pr-3 text-right font-semibold text-gray-800">
                                    ₹{Number(totals.rentals).toLocaleString('en-IN')}
                                  </td>
                                  <td className="py-2 pr-3 text-right font-semibold text-gray-800">
                                    ₹{Number(totals.addons).toLocaleString('en-IN')}
                                  </td>
                                  <td className="py-2 pr-3 text-right font-bold text-emerald-700" data-testid="six-month-grand-total">
                                    ₹{Number(totals.total).toLocaleString('en-IN')}
                                  </td>
                                </>
                              );
                            })()}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Monthly Signup Trend */}
                <div id="stats-members" className="bg-white rounded-xl shadow-lg p-6 scroll-mt-4">
                  <h3 className="font-bold text-gray-800 mb-4">Monthly Signups (Last 6 Months)</h3>
                  <div className="flex items-end gap-4 h-40">
                    {dashboardStats.membership.monthly_trend.map((month, idx) => {
                      const maxCount = Math.max(...dashboardStats.membership.monthly_trend.map(m => m.count), 1);
                      const height = (month.count / maxCount) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-gradient-to-t from-amber-500 to-amber-300 rounded-t-lg transition-all"
                            style={{ height: `${Math.max(height, 5)}%` }}
                          />
                          <p className="text-xs text-gray-600 mt-2">{month.month}</p>
                          <p className="text-sm font-semibold text-gray-800">{month.count}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Stats Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="font-bold text-gray-800 mb-4">Subscription Status</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Active Subscriptions</span>
                        <span className="text-xl font-bold text-green-600">{dashboardStats.overview.active_subscriptions}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Inactive Members</span>
                        <span className="text-xl font-bold text-red-600">{dashboardStats.overview.inactive_subscriptions}</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${dashboardStats.overview.subscription_rate}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-center">{dashboardStats.overview.subscription_rate}% subscription rate</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="font-bold text-gray-800 mb-4">Borrowing Activity</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Currently Borrowed</span>
                        <span className="text-xl font-bold text-blue-600">{dashboardStats.borrowing.currently_borrowed}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Ready for Pickup</span>
                        <span className="text-xl font-bold text-green-600">{dashboardStats.borrowing.reserved_ready}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Pending Approval</span>
                        <span className="text-xl font-bold text-yellow-600">{dashboardStats.borrowing.pending_holds}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rental Stats (pay-per-book) */}
                {dashboardStats.rentals && (
                  <div className="bg-white rounded-xl shadow-lg p-6" data-testid="rentals-stats-section">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <FiBook className="w-5 h-5 text-purple-500" />
                      Pay-Per-Book Rentals
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-700">{dashboardStats.rentals.active_rentals}</p>
                        <p className="text-xs text-purple-700 mt-1">Active right now</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-700">{dashboardStats.rentals.rentals_this_month}</p>
                        <p className="text-xs text-blue-700 mt-1">This month <span className="text-gray-500">(vs {dashboardStats.rentals.rentals_last_month} last)</span></p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-700">₹{Number(dashboardStats.rentals.revenue_this_month || 0).toLocaleString('en-IN')}</p>
                        <p className="text-xs text-green-700 mt-1">Revenue this month</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-700">₹{Number(dashboardStats.rentals.revenue_total || 0).toLocaleString('en-IN')}</p>
                        <p className="text-xs text-amber-700 mt-1">All-time rental revenue</p>
                      </div>
                    </div>
                    {dashboardStats.rentals.active_rentals_list?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Currently out:</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                                <th className="py-2 pr-3">Book</th>
                                <th className="py-2 pr-3">Member</th>
                                <th className="py-2 pr-3">Loan</th>
                                <th className="py-2 pr-3">Paid</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dashboardStats.rentals.active_rentals_list.slice(0, 10).map((r) => (
                                <tr key={r.rental_id} className="border-b border-gray-100 last:border-0">
                                  <td className="py-2 pr-3">
                                    <p className="font-medium text-gray-800 line-clamp-1">{r.book_title || '—'}</p>
                                    <p className="text-xs text-gray-500 line-clamp-1">{(r.book_authors || []).join(', ')}</p>
                                  </td>
                                  <td className="py-2 pr-3">
                                    <p className="text-gray-800">{r.member_name || '—'}</p>
                                    <p className="text-xs text-gray-500">{r.member_email}</p>
                                  </td>
                                  <td className="py-2 pr-3 text-gray-700">{r.rental_period_days} days</td>
                                  <td className="py-2 pr-3 font-medium text-purple-700">₹{r.price}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Book Popularity Section */}
                <div id="stats-books" className="grid md:grid-cols-2 gap-6 scroll-mt-4">
                  {/* Most Popular Books */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <FiTrendingUp className="w-5 h-5 text-green-500" />
                      Most Popular Books
                      <span className="ml-1 text-xs font-normal text-gray-500">(borrows + rentals)</span>
                    </h3>
                    {dashboardStats.book_popularity?.most_popular?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboardStats.book_popularity.most_popular.slice(0, 5).map((book, idx) => (
                          <div key={book.book_id} className="flex items-center gap-3">
                            <span className={`text-lg font-bold ${idx === 0 ? 'text-amber-500' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-amber-700' : 'text-gray-500'}`}>
                              #{idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">{book.title}</p>
                              <p className="text-xs text-gray-500 truncate">{book.authors?.join(', ') || 'Unknown'}</p>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                                {book.total_count ?? book.borrow_count} total
                              </span>
                              {(book.borrow_count != null && book.rental_count != null) && (
                                <p className="text-[10px] text-gray-500 mt-1">
                                  {book.borrow_count} borrows · {book.rental_count} rentals
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No borrow data yet</p>
                    )}
                  </div>

                  {/* Least Popular Books */}
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <FiTrendingDown className="w-5 h-5 text-red-500" />
                      Least Popular Books
                      <span className="ml-1 text-xs font-normal text-gray-500">(borrows + rentals)</span>
                    </h3>
                    {dashboardStats.book_popularity?.least_popular?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboardStats.book_popularity.least_popular.slice(0, 5).map((book, idx) => (
                          <div key={book.book_id} className="flex items-center gap-3">
                            <span className="text-lg font-bold text-gray-400">
                              #{idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">{book.title}</p>
                              <p className="text-xs text-gray-500 truncate">{book.authors?.join(', ') || 'Unknown'}</p>
                            </div>
                            <span className="text-sm font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                              {book.total_count ?? book.borrow_count} total
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No borrow data yet</p>
                    )}
                  </div>
                </div>

                {/* Expired Memberships Section (collapsible) */}
                {dashboardStats.membership?.expired_memberships?.length > 0 && (
                  <details className="bg-white rounded-xl shadow-lg p-6" data-testid="expired-memberships-details">
                    <summary className="font-bold text-gray-800 flex items-center gap-2 cursor-pointer select-none list-none">
                      <FiUserMinus className="w-5 h-5 text-red-500" />
                      Expired Memberships ({dashboardStats.membership.expired_memberships.length})
                      <span className="ml-auto text-xs font-normal text-gray-400">tap to expand ▾</span>
                    </summary>
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-red-50">
                            <th className="text-left py-2 px-3">Member</th>
                            <th className="text-left py-2 px-3">Email</th>
                            <th className="text-left py-2 px-3">Membership #</th>
                            <th className="text-left py-2 px-3">Expired On</th>
                            <th className="text-left py-2 px-3">Days Expired</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.membership.expired_memberships.slice(0, 10).map((member) => (
                            <tr key={member.user_id} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium text-gray-800">{member.name}</td>
                              <td className="py-2 px-3 text-gray-600">{member.email}</td>
                              <td className="py-2 px-3 text-gray-600">{member.membership_number || 'N/A'}</td>
                              <td className="py-2 px-3 text-gray-600">
                                {member.subscription_end_date 
                                  ? new Date(member.subscription_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                  : 'Never subscribed'}
                              </td>
                              <td className="py-2 px-3">
                                {member.days_expired != null ? (
                                  <span className={`font-semibold ${member.days_expired > 30 ? 'text-red-600' : member.days_expired > 7 ? 'text-orange-600' : 'text-yellow-600'}`}>
                                    {member.days_expired} days
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
                
                {/* Borrow Period Rules */}
                <div id="stats-rules" className="bg-white rounded-xl shadow-lg p-6 scroll-mt-4">
                  <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                    <FiClock className="w-5 h-5 text-amber-500" />
                    Borrow Period Rules
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Default borrow period is <span className="font-semibold">21 days</span>. Add rules to set shorter periods for specific categories — or set <span className="font-semibold">0 days</span> to make a category <span className="font-semibold">reference-only</span> (read in the library, no borrowing).
                  </p>

                  {/* Add new rule */}
                  <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-[180px]">
                      <label className="block text-xs text-gray-600 mb-1">Category / Tag</label>
                      <input
                        type="text"
                        value={newRuleCategory}
                        onChange={(e) => setNewRuleCategory(e.target.value)}
                        placeholder="e.g. New Arrivals"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        data-testid="borrow-rule-category-input"
                      />
                    </div>
                    <div className="w-28">
                      <label className="block text-xs text-gray-600 mb-1">Days</label>
                      <input
                        type="number"
                        value={newRuleDays}
                        onChange={(e) => setNewRuleDays(e.target.value)}
                        placeholder="10"
                        min="0"
                        max="365"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        data-testid="borrow-rule-days-input"
                      />
                    </div>
                    <button
                      onClick={handleAddBorrowRule}
                      disabled={addingRule || !newRuleCategory.trim() || !newRuleDays}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
                      data-testid="add-borrow-rule-btn"
                    >
                      {addingRule ? 'Adding...' : 'Add Rule'}
                    </button>
                  </div>

                  {/* Rules list */}
                  {loadingBorrowRules ? (
                    <p className="text-sm text-gray-500">Loading rules...</p>
                  ) : borrowRules.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No custom rules. All books use the default 21-day period.</p>
                  ) : (
                    <div className="space-y-2">
                      {borrowRules.map((rule) => (
                        <div key={rule.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`borrow-rule-${rule.id}`}>
                          {editingRule === rule.id ? (
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-sm font-medium text-gray-800">{rule.category}</span>
                              <input
                                type="number"
                                value={editRuleDays}
                                onChange={(e) => setEditRuleDays(e.target.value)}
                                min="0"
                                max="365"
                                className="w-20 px-2 py-1 border border-amber-300 rounded text-sm"
                                data-testid={`edit-rule-days-${rule.id}`}
                              />
                              <span className="text-sm text-gray-600">days</span>
                              <button
                                onClick={() => handleUpdateBorrowRule(rule.id)}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => { setEditingRule(null); setEditRuleDays(''); }}
                                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-gray-800">{rule.category}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${rule.borrow_days === 0 ? 'bg-indigo-200 text-indigo-800' : 'bg-amber-200 text-amber-800'}`}>
                                  {rule.borrow_days === 0 ? '📖 Reference only' : `${rule.borrow_days} days`}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => { setEditingRule(rule.id); setEditRuleDays(String(rule.borrow_days)); }}
                                  className="p-1.5 text-gray-500 hover:text-amber-700 hover:bg-amber-100 rounded transition-colors"
                                  data-testid={`edit-rule-${rule.id}`}
                                >
                                  <FiEdit className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteBorrowRule(rule.id, rule.category)}
                                  className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  data-testid={`delete-rule-${rule.id}`}
                                >
                                  <FiX className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Subscription Date Audit */}
                {getAuthHeader && (
                  <SubscriptionAuditPanel
                    getAuthHeader={getAuthHeader}
                    onAfterFix={fetchDashboardStats}
                  />
                )}

                {/* Admin Tools */}
                <div id="stats-tools" className="mt-6 pt-6 border-t border-gray-200 scroll-mt-4">
                  <h3 className="font-semibold text-gray-700 mb-3 text-sm">Admin Tools</h3>
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <button
                        onClick={handleFixAllInventory}
                        disabled={fixingInventory}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors disabled:opacity-50"
                        data-testid="fix-inventory-button"
                      >
                        {fixingInventory ? 'Fixing...' : '🔧 Fix Book Inventory'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">Recalculate available copies for all books</p>
                    </div>
                    
                    <div>
                      <button
                        onClick={handleSendRenewalReminders}
                        disabled={sendingRenewalReminders}
                        className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm transition-colors disabled:opacity-50"
                        data-testid="send-renewal-reminders-button"
                      >
                        {sendingRenewalReminders ? 'Sending...' : '📧 Send Renewal Reminders'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">Email members expiring in 7 days</p>
                    </div>
                    
                    <div>
                      <button
                        onClick={handleSendExpiredNotices}
                        disabled={sendingExpiredNotices}
                        className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm transition-colors disabled:opacity-50"
                        data-testid="send-expired-notices-button"
                      >
                        {sendingExpiredNotices ? 'Sending...' : '📬 Send Win-Back Emails'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">Email expired members (last 30 days)</p>
                    </div>
                    
                    <div>
                      <button
                        onClick={handleFixStaffSubscriptions}
                        disabled={fixingStaff}
                        className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm transition-colors disabled:opacity-50"
                        data-testid="fix-staff-subscriptions-button"
                      >
                        {fixingStaff ? 'Fixing...' : '👔 Fix Staff Subscriptions'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">Set all staff to active status</p>
                    </div>
                    
                    <div>
                      <button
                        onClick={handleRecalculateAdvancePayments}
                        disabled={recalculatingPayments}
                        className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm transition-colors disabled:opacity-50"
                        data-testid="recalculate-payments-button"
                      >
                        {recalculatingPayments ? 'Recalculating...' : '📅 Fix Advance Payments'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">Recalculate dates for advance renewals</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <p className="text-gray-600">Failed to load statistics. Please try again.</p>
                <button
                  onClick={fetchDashboardStats}
                  className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
  );
}
